import { supabase } from "@/integrations/supabase/client";

/** Публичный VAPID-ключ — по стандарту он открытый и живёт на клиенте. */
export const VAPID_PUBLIC_KEY =
  "BI5HMOb4-F93vw4tkZNTD05Gk6KAFqXzj0zmC0YMDnOyslZLfgDdm8amTKlkQosxUEKCsqIyvoLZ1YwoH1LicMk";

const SW_URL = "/sw-push.js";

/** Поддерживает ли браузер web push (iOS без установки на «Домой» — нет). */
export function isPushSupported(): boolean {
  if (typeof window === "undefined") return false;
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const normalized = base64.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function keyToB64(sub: PushSubscription, name: "p256dh" | "auth"): string | null {
  const raw = sub.getKey(name);
  if (!raw) return null;
  const bytes = new Uint8Array(raw);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Спрашивает разрешение, подписывает устройство и сохраняет подписку.
 * Возвращает "granted" | "denied" | "unsupported" | "error".
 */
export async function enablePush(
  userId: string,
): Promise<"granted" | "denied" | "unsupported" | "error"> {
  if (!isPushSupported()) return "unsupported";
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return "denied";

    const registration = await navigator.serviceWorker.register(SW_URL);
    await navigator.serviceWorker.ready;

    const existing = await registration.pushManager.getSubscription();
    const sub =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as BufferSource,
      }));

    const p256dh = keyToB64(sub, "p256dh");
    const auth = keyToB64(sub, "auth");
    if (!p256dh || !auth) return "error";

    const { error } = await supabase
      .from("push_subscriptions")
      .upsert(
        { user_id: userId, endpoint: sub.endpoint, p256dh, auth },
        { onConflict: "user_id,endpoint" },
      );
    if (error) {
      console.error("[push] не удалось сохранить подписку:", error.message);
      return "error";
    }
    return "granted";
  } catch (e) {
    console.error("[push] подписка не удалась:", e);
    return "error";
  }
}

export type PushState = "unsupported" | "default" | "denied" | "granted";

/** Синхронное состояние разрешения. Вызывать только в браузере. */
export function getPushState(): PushState {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission as PushState;
}

async function getRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!isPushSupported()) return undefined;
  return (await navigator.serviceWorker.getRegistration(SW_URL)) ?? undefined;
}

/** Есть ли активная push-подписка на этом устройстве. */
export async function isSubscribedOnThisDevice(): Promise<boolean> {
  try {
    const reg = await getRegistration();
    if (!reg) return false;
    return !!(await reg.pushManager.getSubscription());
  } catch (e) {
    console.error("[push] не удалось проверить подписку:", e);
    return false;
  }
}

/** Отписывает текущее устройство и удаляет его подписку из базы. */
export async function disablePush(userId: string): Promise<boolean> {
  try {
    const reg = await getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (!sub) return true;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    const { error } = await supabase
      .from("push_subscriptions")
      .delete()
      .eq("user_id", userId)
      .eq("endpoint", endpoint);
    if (error) {
      console.error("[push] не удалось удалить подписку:", error.message);
      return false;
    }
    return true;
  } catch (e) {
    console.error("[push] отписка не удалась:", e);
    return false;
  }
}
